import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang, pick } from "../context/LanguageContext";
import { t } from "../data/translations";
import { resolveImg } from "../lib/api";

export const Menu = ({ items, categories }) => {
  const { lang } = useLang();
  const [active, setActive] = useState(categories[0] || "");

  const filtered = items.filter((i) => i.category === active);

  return (
    <section id="menu" className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="menu-section">
      <div className="mb-12">
        <p className="text-brand-gold uppercase tracking-[0.3em] text-xs mb-4">{pick(t.menu.kicker, lang)}</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-2xl text-balance">
          {pick(t.menu.title, lang)}
        </h2>
      </div>

      {/* category tabs */}
      <div className="flex flex-wrap gap-3 mb-12 border-b border-brand-line pb-6" data-testid="menu-tabs">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setActive(c)}
            data-testid={`menu-tab-${c.replace(/\s+/g, "-").toLowerCase()}`}
            className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 ${
              active === c ? "bg-brand-gold text-brand-bg" : "border border-brand-line text-brand-muted hover:text-brand-text hover:border-brand-gold/50"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-x-14 gap-y-8"
        >
          {filtered.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
              className="group flex items-center gap-5 py-4 border-b border-brand-line/60"
              data-testid={`menu-item-${item.id}`}
            >
              {item.image_url ? (
                <img
                  src={resolveImg(item.image_url)}
                  alt={pick({ en: item.name_en, es: item.name_es }, lang)}
                  className="h-20 w-20 rounded-2xl object-cover shrink-0 ring-1 ring-white/10 group-hover:ring-brand-gold/50 transition-[box-shadow] duration-300"
                />
              ) : (
                <div className="h-20 w-20 rounded-2xl shrink-0 bg-brand-surface flex items-center justify-center text-brand-gold font-display text-2xl">✦</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-3">
                  <h3 className="font-display text-xl sm:text-2xl leading-tight">{pick({ en: item.name_en, es: item.name_es }, lang)}</h3>
                  <span className="flex-1 border-b border-dotted border-brand-line/70 translate-y-[-4px]" />
                  <span className="font-display text-lg text-brand-gold shrink-0">{item.price}</span>
                </div>
                <p className="mt-1 text-sm text-brand-muted leading-snug">{pick({ en: item.description_en, es: item.description_es }, lang)}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>

      <p className="mt-10 text-xs text-brand-muted/70 italic">{pick(t.menu.priceNote, lang)}</p>
    </section>
  );
};
